import { HttpClient } from '../api/httpclient.service';

export interface LessonStep {
  step_number: number;
  title: string;
  teacher_content?: string;
  chart_url?: string;
  video_url?: string;
  quiz_json?: string;
  quiz?: any;
  teacher_summary?: string;
}

export interface LessonPhasePayload {
  id: number;
  lesson_id: number;
  step_num: number;
  exercise_list?: number[];
}

export interface LessonPayload {
  topic_id: number;
  level_id?: number;
  sub_topic_id?: number;
  instructions?: string;
  lesson_video_url?: string;
  content: LessonStep[];
  is_active: boolean;
  phases?: LessonPhasePayload[];
}

export class LessonService extends HttpClient {
  constructor() {
    super();
  }

  private static _metadataCache = new Map<string, any>();
  private static _activePromises = new Map<string, Promise<any>>();

  private async _deduplicate<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
    // 1. Check cache first for metadata
    if (LessonService._metadataCache.has(key)) {
      return LessonService._metadataCache.get(key);
    }

    // 2. Return active promise if exists
    if (LessonService._activePromises.has(key)) {
      return LessonService._activePromises.get(key);
    }

    // 3. Create new promise
    const promise = fetcher().finally(() => {
      LessonService._activePromises.delete(key);
    });

    LessonService._activePromises.set(key, promise);
    
    // 4. Cache metadata indefinitely once resolved
    if (key.startsWith('meta:')) {
      const result = await promise;
      LessonService._metadataCache.set(key, result);
    }

    return promise;
  }

  public async getLessonLevels() {
    return this._deduplicate('meta:levels', () => 
      this.get<{ id: number; name: string; code: number }[]>('/lessons/levels')
    );
  }

  public async getLessonTopics(levelId: number) {
    return this._deduplicate(`meta:topics:${levelId}`, () => 
      this.get<{ id: number; name: string; code: number }[]>(`/lessons/topics?level_id=${levelId}`)
    );
  }

  public async getLessonSubTopics(levelId: number, topicId: number) {
    return this._deduplicate(`meta:subtopics:${levelId}:${topicId}`, () => 
      this.get<{ id: number; name: string; code: number }[]>(`/lessons/sub-topics?level_id=${levelId}&topic_id=${topicId}`)
    );
  }

  public async saveLesson(payload: LessonPayload) {
    try {
      const response = await this.post('/lessons/', payload);
      return response;
    } catch (error) {
      console.error('Error saving lesson:', error);
      throw error;
    }
  }

  public async getLesson(lessonId: number) {
    return this._deduplicate(`lesson:${lessonId}`, () => 
      this.get(`/lessons/${lessonId}`)
    );
  }

  public async getLessonByTopic(topicId: number, levelId: number, subTopicId?: number) {
    return this._deduplicate(`lesson:topic:${topicId}:${levelId}:${subTopicId || 0}`, () => {
      const queryParams = new URLSearchParams();
      queryParams.append('level_id', levelId.toString());
      if (subTopicId && subTopicId !== -1) queryParams.append('sub_topic_id', subTopicId.toString());

      return this.get(`/lessons/by-topic/${topicId}?${queryParams.toString()}`).catch(error => {
        if (error.response && error.response.status === 404) return null;
        throw error;
      });
    });
  }

  public async shareLesson(topicId: number, levelId: number, subTopicId?: number, regenerate: boolean = false) {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('level_id', levelId.toString());
      queryParams.append('regenerate', regenerate.toString());
      if (subTopicId && subTopicId !== -1) queryParams.append('sub_topic_id', subTopicId.toString());

      const response = await this.post(`/lessons/by-topic/${topicId}/share?${queryParams.toString()}`, {});
      return response;
    } catch (error: any) {
      console.error('Error sharing lesson:', error);
      throw error;
    }
  }

  public async getSharedLesson(token: string, pin: string) {
    try {
      const response = await this.get(`/lessons/shared/${token}?pin=${pin}`);
      return response;
    } catch (error: any) {
      console.error('Error fetching shared lesson:', error);
      throw error;
    }
  }

  public async generateContentWithAI(prompt: string, context: string = '', topic_id?: number, level_id?: number, sub_topic_id?: number, section_type: string = 'teacher_content', section_title?: string, image_base64?: string) {
    try {
      const payload: any = { prompt, context, section_type, section_title };
      if (image_base64) {
        payload.image_base64 = image_base64;
      }
      if (topic_id) {
        payload.topic_id = topic_id;
      }
      if (level_id) {
        payload.level_id = level_id;
      }
      if (sub_topic_id && sub_topic_id !== -1) {
        payload.sub_topic_id = sub_topic_id;
      }
      const response = await this.post('/lessons/generate', payload);
      return response.content;
    } catch (error) {
      console.error('Error generating AI content:', error);
      throw error;
    }
  }

  public async getStepExplanation(stepContent: string, history: any[]) {
    try {
      const payload = {
        content: stepContent,
        chat_history: history
      };
      // For now we don't call the actual endpoint, we just return the payload structure
      // return await this.post('/lessons/explain-step', payload);
      return payload;
    } catch (error) {
      console.error('Error fetching step explanation:', error);
      throw error;
    }
  }

  public async getQuizClue(quizData: any, history: any[]) {
    try {
      const payload = {
        quiz: quizData,
        chat_history: history
      };
      // return await this.post('/lessons/quiz-clue', payload);
      return payload;
    } catch (error) {
      console.error('Error fetching quiz clue:', error);
      throw error;
    }
  }

  public async getStepSectionContent(topicId: number, levelId: number, stepNumber: number, section: string, subTopicId?: number) {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('level_id', levelId.toString());
      if (subTopicId && subTopicId !== -1) queryParams.append('sub_topic_id', subTopicId.toString());

      const response = await this.get(`/lessons/by-topic/${topicId}/step/${stepNumber}/section/${section}?${queryParams.toString()}`);
      return response.content;
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        return null;
      }
      console.error('Error loading section content:', error);
      throw error;
    }
  }

  public async generateChartWithAI(prompt: string, topic_id: number, level_id: number, sub_topic_id: number | undefined, step_number: number, teacher_content?: string, image_base64?: string, existing_chart_url?: string) {
    try {
      const payload: any = {
        prompt,
        topic_id,
        level_id,
        step_number,
        teacher_content,
        image_base64,
        existing_chart_url
      };
      if (sub_topic_id && sub_topic_id !== -1) payload.sub_topic_id = sub_topic_id;

      const response = await this.post<{ chart_url: string }>('/lessons/generate-lesson-chart', payload);
      return response.chart_url;
    } catch (error) {
      console.error('Error generating chart with AI:', error);
      throw error;
    }
  }

  public async uploadManualChart(topic_id: number, level_id: number, sub_topic_id: number | undefined, step_number: number, image_base64: string) {
    try {
      const payload: any = {
        topic_id,
        level_id,
        step_number,
        image_base64,
        prompt: "manual_upload" // Satisfy GenerateRequest schema if needed
      };
      if (sub_topic_id && sub_topic_id !== -1) payload.sub_topic_id = sub_topic_id;

      const response = await this.post<{ chart_url: string }>('/lessons/upload-manual-chart', payload);
      return response.chart_url;
    } catch (error) {
      console.error('Error uploading manual chart:', error);
      throw error;
    }
  }

  public async saveLessonPhase(payload: {
    topic_id: number;
    level_id: number;
    sub_topic_id?: number;
    step_num: number;
    exercise_list?: { id: number; name: string }[];
  }) {
    try {
      const response = await this.post('/lessons/phase', payload);
      return response;
    } catch (error) {
      console.error('Error saving lesson phase:', error);
      throw error;
    }
  }

  public async deleteLessonPhase(topic_id: number, level_id: number, step_num: number, sub_topic_id?: number) {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('topic_id', topic_id.toString());
      queryParams.append('level_id', level_id.toString());
      queryParams.append('step_num', step_num.toString());
      if (sub_topic_id !== undefined && sub_topic_id !== -1) {
        queryParams.append('sub_topic_id', sub_topic_id.toString());
      }

      const response = await this.delete(`/lessons/phase?${queryParams.toString()}`);
      return response;
    } catch (error) {
      console.error('Error deleting lesson phase:', error);
      throw error;
    }
  }

  public async startLessonAutogen(payload: {
    level_id: number;
    topic_id: number;
    sub_topic_id?: number;
    master_key: string;
  }) {
    try {
      const response = await this.post<{
        job_id: string;
        status: string;
        topic_name: string;
        unit_level: number;
        sub_topic_name?: string;
      }>('/lessons/autogen/start', payload);
      return response;
    } catch (error) {
      console.error('Error starting lesson autogen:', error);
      throw error;
    }
  }

  public async getLessonAutogenStatus(jobId: string) {
    try {
      const response = await this.get<{
        job_id: string;
        status: 'pending' | 'in_progress' | 'completed' | 'failed';
        created_at: string;
        completed_at?: string;
        topic_name: string;
        unit_level: number;
        sub_topic_name?: string;
        stage_key?: string;
        stage_params?: Record<string, any>;
        progress_message?: string;
        sub_topics_progress?: Array<{
          name: string;
          status: string;
          stage_key?: string;
          stage_params?: Record<string, any>;
          message?: string;
          step_count?: number;
        }>;
        results?: Array<{
          sub_topic_name: string;
          sub_topic_id: number;
          status: string;
          step_count: number;
          lesson_id?: number;
          error?: string;
        }>;
        summary?: Record<string, number>;
        error?: string;
      }>(`/lessons/autogen/status/${jobId}`);
      return response;
    } catch (error) {
      console.error('Error fetching lesson autogen status:', error);
      throw error;
    }
  }
}

